import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-hot-toast';

const API = 'https://api.glowytics.com/api/insights';

const SEV = {
  critical:  { ring: 'border-rose-200',   bg: 'bg-rose-50',    dot: 'bg-rose-500',    text: 'text-rose-700',    label: 'Kritik' },
  warning:   { ring: 'border-amber-200',  bg: 'bg-amber-50',   dot: 'bg-amber-500',   text: 'text-amber-700',   label: 'Uyarı' },
  positive:  { ring: 'border-emerald-200',bg: 'bg-emerald-50', dot: 'bg-emerald-500', text: 'text-emerald-700', label: 'Fırsat' },
  info:      { ring: 'border-sky-200',    bg: 'bg-sky-50',     dot: 'bg-sky-500',     text: 'text-sky-700',     label: 'Bilgi' },
};

const SOURCE_BADGE = {
  meta: 'bg-indigo-100 text-indigo-700',
  'google ads': 'bg-blue-100 text-blue-700',
  'rtb house': 'bg-fuchsia-100 text-fuchsia-700',
  tiktok: 'bg-gray-800 text-white',
  pinterest: 'bg-red-100 text-red-700',
  criteo: 'bg-orange-100 text-orange-700',
};

const fmtTRY = (n) => Number(n || 0).toLocaleString('tr-TR', { maximumFractionDigits: 0 });

function Evidence({ ev }) {
  if (!ev || (!ev.campaigns?.length && !ev.finer)) return null;
  const camps = (ev.campaigns || []).slice(0, 4);
  return (
    <div className="mt-2 rounded-lg bg-white/60 border border-gray-100 px-3 py-2">
      {camps.length > 0 && (
        <table className="w-full text-[11px] text-gray-600">
          <thead>
            <tr className="text-gray-400">
              <th className="text-left font-medium pb-1">Kampanya</th>
              <th className="text-right font-medium pb-1">Harcama</th>
              <th className="text-right font-medium pb-1">ROAS</th>
              <th className="text-right font-medium pb-1">Δ</th>
            </tr>
          </thead>
          <tbody>
            {camps.map((c, idx) => (
              <tr key={idx} className="border-t border-gray-50">
                <td className="py-0.5 pr-2 truncate max-w-[220px]" title={c.name}>{c.name}</td>
                <td className="py-0.5 text-right tabular-nums">{fmtTRY(c.recent_spend)}₺</td>
                <td className="py-0.5 text-right tabular-nums">{Number(c.recent_roas).toFixed(1)}x</td>
                <td className={`py-0.5 text-right tabular-nums ${c.roas_delta > 0 ? 'text-emerald-600' : c.roas_delta < 0 ? 'text-rose-600' : 'text-gray-400'}`}>
                  {c.roas_delta == null ? '—' : `${c.roas_delta > 0 ? '+' : ''}${Math.round(c.roas_delta * 100)}%`}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {ev.finer && ev.finer.items?.length > 0 && (
        <div className="mt-2 pt-2 border-t border-gray-100">
          <p className="text-[10px] text-gray-400 mb-1">
            {ev.finer.level === 'keyword' ? 'Öne çıkan keyword' : 'Öne çıkan ürün'} — {ev.finer.campaign}
          </p>
          <div className="flex flex-wrap gap-1.5">
            {ev.finer.items.slice(0, 5).map((it, idx) => (
              <span key={idx} className="text-[10px] bg-gray-100 text-gray-600 rounded px-1.5 py-0.5" title={`${fmtTRY(it.spend)}₺ · ROAS ${it.roas}x`}>
                {it.label} · {Number(it.roas).toFixed(1)}x
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Card({ ins, onDismiss }) {
  const s = SEV[ins.severity] || SEV.info;
  const rec = ins.ai_recommendation || ins.recommended_action;
  const impact = ins.ai_impact || ins.impact_hint;
  return (
    <div className={`rounded-xl border ${s.ring} ${s.bg} p-4 flex gap-3`}>
      <div className={`mt-1 h-2.5 w-2.5 rounded-full flex-shrink-0 ${s.dot}`} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap mb-1">
          <span className={`text-[10px] font-semibold uppercase tracking-wide ${s.text}`}>{s.label}</span>
          {ins.source && (
            <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${SOURCE_BADGE[ins.source] || 'bg-gray-100 text-gray-600'}`}>
              {ins.source.toUpperCase()}
            </span>
          )}
        </div>
        <p className="text-sm font-semibold text-gray-800">{ins.title}</p>

        {/* AI advisory narrative (falls back to the rule-based detail) */}
        {ins.ai_narrative ? (
          <p className="text-[12px] text-gray-700 mt-1 leading-relaxed">
            <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-violet-600 mr-1 align-middle">✦ Glowy</span>
            {ins.ai_narrative}
          </p>
        ) : (
          <p className="text-[12px] text-gray-600 mt-1 leading-relaxed">{ins.detail}</p>
        )}

        {impact && (
          <p className="text-[11px] font-medium text-gray-700 mt-1.5 bg-white/70 rounded px-2 py-1 inline-block border border-gray-100">
            📈 {impact}
          </p>
        )}

        <Evidence ev={ins.evidence} />

        {rec && (
          <div className="mt-2 flex items-start gap-1.5 text-[12px] text-gray-700 bg-white/70 rounded-lg px-3 py-2 border border-gray-100">
            <span className="text-gray-400">⇒</span>
            <span>{rec}</span>
          </div>
        )}
      </div>
      <button
        onClick={() => onDismiss(ins.id)}
        title="Kapat"
        className="self-start text-gray-300 hover:text-gray-500 text-lg leading-none px-1"
      >×</button>
    </div>
  );
}

export default function InsightsFeed() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await fetch(`${API}/overview`, {
        credentials: 'include',
        headers: { 'X-Brand-ID': localStorage.getItem('currentBrandId') || '' },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setData(await res.json());
    } catch (err) {
      setError(err.message || 'Insight verisi alınamadı');
      toast.error('Insight verisi alınamadı. ' + (err.message || ''));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const dismiss = async (id) => {
    try {
      const res = await fetch(`${API}/${id}/dismiss`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'X-Brand-ID': localStorage.getItem('currentBrandId') || '' },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setData((d) => d && {
        ...d,
        risks: d.risks.filter((r) => r.id !== id),
        opportunities: d.opportunities.filter((r) => r.id !== id),
      });
    } catch (err) { toast.error('Kapatılamadı. ' + (err.message || '')); }
  };

  const counts = data?.counts || { total: 0, critical: 0, warning: 0, opportunity: 0 };

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-1">
        <h1 className="text-2xl font-bold text-gray-800">Insights</h1>
        <button
          onClick={fetchData}
          disabled={loading}
          className="text-sm px-3 py-1.5 rounded-lg bg-gray-800 text-white hover:bg-gray-700 disabled:opacity-50"
        >{loading ? 'Taranıyor…' : '↻ Yenile'}</button>
      </div>
      <p className="text-sm text-gray-500 mb-5">
        Glowytics senin için izliyor — son 7 günü önceki 21 güne göre tarayıp riskleri ve fırsatları öne çıkarır.
        {data?.asOfDate && <span className="ml-1">Veri: {String(data.asOfDate).slice(0, 10)}</span>}
      </p>

      {/* summary chips */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        {[
          ['Toplam', counts.total, 'text-gray-800'],
          ['Kritik', counts.critical, 'text-rose-600'],
          ['Uyarı', counts.warning, 'text-amber-600'],
          ['Fırsat', counts.opportunity, 'text-emerald-600'],
        ].map(([k, v, c]) => (
          <div key={k} className="rounded-xl border bg-white p-3">
            <p className="text-[11px] text-gray-500">{k}</p>
            <p className={`text-2xl font-bold ${c}`}>{v}</p>
          </div>
        ))}
      </div>

      {loading && !data && <p className="text-gray-400 text-sm">Yükleniyor…</p>}
      {error && <p className="text-rose-600 text-sm">{error}</p>}

      {data && !loading && counts.total === 0 && (
        <div className="rounded-xl border border-dashed p-10 text-center text-gray-400">
          🎉 Şu an dikkat gerektiren bir şey yok — tüm kanallar normal seyrediyor.
        </div>
      )}

      {data?.risks?.length > 0 && (
        <section className="mb-6">
          <h2 className="text-sm font-semibold text-gray-700 mb-2">⚠️ Riskler</h2>
          <div className="space-y-3">
            {data.risks.map((i) => <Card key={i.id} ins={i} onDismiss={dismiss} />)}
          </div>
        </section>
      )}

      {data?.opportunities?.length > 0 && (
        <section>
          <h2 className="text-sm font-semibold text-gray-700 mb-2">🚀 Fırsatlar</h2>
          <div className="space-y-3">
            {data.opportunities.map((i) => <Card key={i.id} ins={i} onDismiss={dismiss} />)}
          </div>
        </section>
      )}
    </div>
  );
}
